import { useEffect, useState } from 'react';

import { appParams } from '@playbooks/api';
import { Feedback } from '@playbooks/molecules/feedbacks';
import { SearchForm } from '@playbooks/molecules/forms';
import { Pagination } from '@playbooks/molecules/paginations';
import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Tabs, TabsList, TabsTrigger } from '@playbooks/ui/components/tabs';

import { AgentDesignPalette } from 'components/agent/agent-design-palette';
import { useStore } from 'contexts';

const AgentDesignFlowSelect = ({
	selectedId,
	onClose,
	onAdd,
	onEdit,
	onSelect,
	onSave,
	canSave,
	libraryOnly = true,
	canManageResources = true,
}) => {
	const [designs, setDesigns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [params, setParams] = useState<any>({ ...appParams, view: 'public', pageSize: 10 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 10, totalRecords: 0 });
	const [error, setError] = useState('');
	const store = useStore();

	// Hooks
	useEffect(() => {
		let active = true;
		setLoading(true);
		setError('');
		store
			.query({ url: libraryOnly ? '/workspace/designs' : '/agent-designs', params })
			.then(response => {
				if (!active) return;
				setDesigns(response.data || []);
				setMeta(response.meta || { page: params.page, pageSize: params.pageSize, totalRecords: 0 });
			})
			.catch(nextError => active && setError(nextError?.message || 'Designs could not be loaded.'))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [params, store]);

	// Methods
	const onDelete = async design => {
		if (!window.confirm(`Remove ${design.name} from this workspace?`)) return;
		setError('');
		try {
			await store.deleteRecord({ url: `/agent-designs/${design.uuid}` });
			setDesigns(current => current.filter(item => item.id !== design.id));
		} catch (nextError) {
			setError(nextError?.message || 'The design could not be removed.');
		}
	};
	const onView = view => setParams(current => ({ ...current, page: 0, view }));
	const emptyTitle = params.query
		? `No ${params.view} designs found`
		: params.view === 'private'
			? 'No private designs yet'
			: 'No public designs available';

	// Render
	return (
		<>
			<DialogHeader>
				<DialogTitle>Choose a design</DialogTitle>
			</DialogHeader>
			<Tabs value={params.view} onValueChange={onView} className='gap-0 border-b px-4'>
				<TabsList>
					<TabsTrigger value='public'>Public</TabsTrigger>
					<TabsTrigger value='private'>Private</TabsTrigger>
				</TabsList>
			</Tabs>
			<div className='flex-between gap-3 border-b p-4'>
				<SearchForm
					id='agent_design_flow_search'
					query={params.query || ''}
					setQuery={query => setParams({ ...params, page: 0, query })}
					placeholder='Search designs...'
					size='default'
					className='grow'
				/>
				<Button size='default' disabled={!canManageResources} onClick={onAdd}>
					Add New
				</Button>
			</div>
			<div className='min-h-96 py-2'>
				{loading ? (
					<Feedback
						layout='modal'
						icon='spinner'
						title='Loading designs'
						text={`Finding ${params.view} designs.`}
						className='min-h-80'
					/>
				) : error ? (
					<Feedback
						layout='modal'
						icon='triangle-exclamation'
						title='Designs unavailable'
						text={error}
						className='min-h-80'
					/>
				) : designs.length ? (
					<div className='grid gap-1'>
						{designs.map(design => (
							<div key={design.id} className='flex items-center gap-1'>
								<Button
									variant='ghost'
									data-active={selectedId === design.id || undefined}
									onClick={() => onSelect(design)}
									className='h-auto min-h-12 min-w-0 grow justify-start gap-3 px-3 py-2 text-left'>
									<span className='min-w-0 grow'>
										<span className='block truncate text-sm font-medium'>{design.name}</span>
										<span className='block truncate text-xs font-normal text-foreground-body'>
											{design.description}
										</span>
										<span className='block truncate text-xs font-normal text-foreground-subtle'>
											{design.owner?.name || 'Playbooks'} · {design.sourceType}
										</span>
										{design.tags?.length ? (
											<span className='block truncate text-xs font-normal text-foreground-subtle'>
												{design.tags
													.slice(0, 3)
													.map(tag => tag.name)
													.join(' · ')}
											</span>
										) : null}
									</span>
									<AgentDesignPalette colors={design.colors} />
								</Button>
								{design.isOwner && canManageResources ? (
									<Button variant='ghost' size='sm' onClick={() => onEdit(design)}>
										Edit
									</Button>
								) : null}
								{design.isOwner && canManageResources ? (
									<Button variant='ghost' size='sm' onClick={() => onDelete(design)}>
										Remove
									</Button>
								) : null}
							</div>
						))}
					</div>
				) : (
					<Feedback
						layout='modal'
						icon='brush'
						title={emptyTitle}
						text={
							params.query
								? 'Try a different search.'
								: params.view === 'private'
									? 'Create or import a private design for this workspace.'
									: 'Public agent designs will appear here.'
						}
						className='min-h-80'
					/>
				)}
			</div>
			<div className='border-t p-3'>
				<Pagination
					layout='table'
					meta={meta}
					params={params}
					setParams={setParams}
					taskRunning={loading}
					totalLabel='designs'
				/>
			</div>
			<DialogFooter>
				<Button variant='secondary' onClick={onClose}>
					Cancel
				</Button>
				<Button disabled={!canSave} onClick={onSave}>
					Save
				</Button>
			</DialogFooter>
		</>
	);
};

export { AgentDesignFlowSelect };
